import { Divider, InputLabel, Stack, styled } from "~/common/mui";
import SearchInput from "~/common/components/SearchInput";
import { UnitModeToggle } from "~/domains/userPreferences/components/UnitModeToggle";
import { FilterSortChip } from "~/features/search/components/common/mobileChips/FilterSortChip";
import { TechCategoryChip } from "~/features/search/components/common/mobileChips/TechCategoryChip";
import { ViewChip } from "~/features/search/components/common/mobileChips/ViewChip";
import { SearchDisplayMode } from "~/features/search/models/models";
import ReduxSortingPicker from "../../common/ReduxSortingPicker";
import { DisplayModeButtonList } from "../../common/SearchResultsDisplayMode/SearchResultsDisplayMode";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import type { UnitMode } from "~/domains/userPreferences/types/UnitMode";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { useDispatch, useSelector } from "react-redux";
import { handleMissingSortAfterTabChangeThunk, updateSearchText } from "~/features/search/redux/actions";
import { trackTreesInteraction } from "~/app/analytics/ga/ga";
import { selectSearchQuery } from "~/features/search/redux/searchSelectors";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConnectedNewDesignToggle } from "../views/ListView/components/NewDesignToggle/NewDesignToggle";

interface TopFilterBarProps {
  unitMode: UnitMode;
  displayMode: SearchDisplayMode;
  setUnitMode: (unitMode: UnitMode) => void;
  setDisplayMode: (displayMode: SearchDisplayMode) => void;
  newDesign: boolean;
  canShowNewDesignTabular: boolean;
}

export const TopFilterBar = ({
  unitMode,
  displayMode,
  setUnitMode,
  setDisplayMode,
  newDesign,
  canShowNewDesignTabular,
}: TopFilterBarProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktop = useIsMinDesktopSize();
  const pageType = useSearchPageType();

  const showTreeButton = pageType !== SearchPageType.Campaign;
  const showQueryInput = pageType === SearchPageType.Campaign;
  const gaInteractionType = pageType === SearchPageType.SEARCH ? "Search" : "PLP";
  const showNewDesignToggle = pageType !== SearchPageType.Campaign;
  const showChangeViewToggle = pageType !== SearchPageType.Campaign && (!newDesign || canShowNewDesignTabular);

  const text = useSelector(selectSearchQuery);
  useEffect(() => {
    setSearchQuery(text);
  }, [text]);
  const [searchQuery, setSearchQuery] = useState(text);

  const newDesignToggle = <ConnectedNewDesignToggle />;

  let bar;
  if (!isDesktop) {
    bar = (
      <>
        <Stack
          direction="row"
          sx={{
            gap: 1,
            overflow: "auto",
            scrollbarWidth: "none",
          }}
        >
          <ViewChip
            initialUnit={unitMode}
            initialViewMode={displayMode}
            newDesign={newDesign}
            applyValues={(unit, mode) => {
              setUnitMode(unit);
              setDisplayMode(mode);
            }}
          />
          <Divider flexItem orientation="vertical" />

          <FilterSortChip />

          <TechCategoryChip index={0} />
          <TechCategoryChip index={1} />
        </Stack>
        {newDesignToggle}
      </>
    );
  } else {
    const funcDisplayMode = (targetDisplayMode: SearchDisplayMode) => {
      let display: "List" | "Tile" | "Tree";
      if (targetDisplayMode === SearchDisplayMode.LIST) {
        display = "Tile";
        dispatch(handleMissingSortAfterTabChangeThunk());
      } else if (targetDisplayMode === SearchDisplayMode.TABULAR) {
        display = "List";
      } else {
        display = "Tree";
      }
      trackTreesInteraction(gaInteractionType, display);
      setDisplayMode(targetDisplayMode);
    };

    bar = (
      <>
        {showNewDesignToggle && newDesignToggle}

        <ActionBar>
          {showChangeViewToggle && (
            <DisplayModeButtonList
              displayMode={displayMode}
              setDisplayMode={funcDisplayMode}
              showSeriesTree={!newDesign && showTreeButton}
            />
          )}

          <UnitModeToggle
            className={"scha__srp__action-bar__unit-toggle"}
            value={unitMode}
            onChange={setUnitMode}
            fullWidth={true}
          />

          {!showChangeViewToggle && <div />}

          {displayMode === SearchDisplayMode.LIST && (
            <div>
              <InputLabel shrink>{t("searchPage.sort.title")}</InputLabel>
              <ReduxSortingPicker />
            </div>
          )}
        </ActionBar>
      </>
    );
  }

  const submit = () => {
    dispatch(updateSearchText(searchQuery));
  };

  return (
    <>
      {bar}
      {showQueryInput && (
        <SearchInput
          onSearch={submit}
          label={t("searchPage.searchBox.placeholder")}
          onChange={setSearchQuery}
          value={searchQuery}
          sx={{ width: "354px" }}
          size="small"
        />
      )}
    </>
  );
};

const ActionBar = styled("div")({
  display: "grid",
  gridTemplateColumns: "minmax(max-content, 1fr) minmax(max-content, 1fr) 1fr",
  gap: "24px",
  whiteSpace: "nowrap",
});
